/*
 * SHOGun GPRO, based on https://terrestris.github.io/shogun/
 *
 * Copyright © 2022-present terrestris GmbH & Co. KG
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0.txt
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */
import React, { useEffect, useState } from 'react';

import {
  MailOutlined
} from '@ant-design/icons';
import { Input, Tooltip, Button } from 'antd';
import _isNil from 'lodash/isNil';
import BaseLayer from 'ol/layer/Base';
import ImageLayer from 'ol/layer/Image';
import TileLayer from 'ol/layer/Tile';
import { useTranslation } from 'react-i18next';

import { PermalinkUtil } from '@terrestris/ol-util';
import { useMap } from '@terrestris/react-util';

import CopyButton from '../CopyButton/CopyButton';

import './SharePanel.less';

interface DefaultSharePanelProps { }

export interface SharePanelProps extends Partial<DefaultSharePanelProps> { }

export const SharePanel: React.FC<SharePanelProps> = () => {

  const map = useMap();
  const { t } = useTranslation();

  const [link, setLink] = useState<string>();

  useEffect(() => {
    if (_isNil(map)) {
      return;
    }

    map.getView().on('change', () => {
      setLink(PermalinkUtil.getLink(
        map,
        ',',
        undefined,
        (l: BaseLayer) => l && (l instanceof TileLayer || l instanceof ImageLayer) && l.getVisible()
      ));
    });
    if (_isNil(link)) {
      // initially set the link
      setLink(
        PermalinkUtil.getLink(
          map,
          ',',
          undefined,
          (l: BaseLayer) => l && (l instanceof TileLayer || l instanceof ImageLayer) && l.getVisible()
        )
      );
    }
  }, [link, map]);

  // TODO: these could be props
  const mailSubject = 'Geoportal Raumordnung Kartenviewer';
  const mailBody = `Hey,\r\nsieh dir an was ich im Geoportal Raumordnung Baden-Württemberg gefunden habe:\r\n${link}`;

  function onMailClick() {
    const mailToUrl = new URL('mailto:');
    mailToUrl.searchParams.set('subject', mailSubject);
    mailToUrl.searchParams.set('body', mailBody);
    window.open(mailToUrl.toString().replace(/\+/g, '%20'));
  }

  if (_isNil(link)) {
    return <></>;
  }

  return (
    <div className="share-panel">
      <div className="icons">
        <Tooltip title={t('SharePanel.mailTooltip')}>
          <Button
            icon={<MailOutlined />}
            onClick={onMailClick}
          />
        </Tooltip>
      </div>
      <div className="link">
        <Input value={link} readOnly />
        <CopyButton link={link} />
      </div>
    </div>
  );
};

export default SharePanel;
